import React, { useState } from 'react';
import { useTeam } from '../../context/TeamContext';
import { useProducts } from '../../context/ProductsContext';
import { useCalculator } from '../../context/CalculatorContext';
import {
  calculateTeamFTE,
  condenseActivityList,
  generateCondensedJustification
} from '../../utils/teamCalculations';
import Card from '../Common/Card';
import Button from '../Common/Button';
import Input from '../Common/Input';
import { useToast } from '../Common/Toast';

const AVPView = () => {
  const { teams, currentTeam, addTeam, removeTeam } = useTeam();
  const { products } = useProducts();
  const { inputs } = useCalculator();
  const { showToast } = useToast();
  
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [avpName, setAvpName] = useState('');

  // Get all director teams with type checking
  const directorTeams = Array.isArray(teams) ? teams.filter(t => t.role === 'director') : [];

  // Calculate rollup data for each director
  const directorRollups = directorTeams.map(director => {
    // Get all manager teams under this director
    const managerTeams = Array.isArray(teams) ? teams.filter(
      t => t.role === 'manager' && t.parentTeamId === director.id
    ) : [];

    // Get all activities from manager teams
    const allActivities = managerTeams.flatMap(team => 
      products.filter(p => team.activities && team.activities.includes(p.id))
    );

    // Calculate total FTE
    const totalFTE = managerTeams.reduce((sum, team) => {
      const teamActivities = products.filter(p => 
        team.activities && team.activities.includes(p.id)
      );
      return sum + calculateTeamFTE(teamActivities, inputs);
    }, 0);

    // Condense activities
    const condensedActivities = condenseActivityList(allActivities);

    // Generate condensed justification
    const condensedJustification = generateCondensedJustification(
      managerTeams,
      allActivities,
      totalFTE
    );

    return {
      director,
      managerCount: managerTeams.length,
      totalActivities: allActivities.length,
      totalFTE,
      condensedActivities,
      condensedJustification
    };
  });

  // Grand totals
  const grandTotalFTE = directorRollups.reduce((sum, d) => sum + d.totalFTE, 0);
  const grandTotalActivities = directorRollups.reduce((sum, d) => sum + d.totalActivities, 0);
  const grandTotalManagers = directorRollups.reduce((sum, d) => sum + d.managerCount, 0);

  const handleCreateTeam = async () => {
    if (!teamName || !avpName) {
      showToast('Please enter team name and AVP name', 'error');
      return;
    }

    const result = await addTeam({
      name: teamName,
      managerName: avpName,
      managerEmail: '',
      role: 'avp',
      parentTeamId: null,
      activities: []
    });

    if (result.success) {
      showToast('AVP team created successfully!');
      setShowCreateTeam(false);
      setTeamName('');
      setAvpName('');
    } else {
      showToast('Failed to create team', 'error');
    }
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    if (window.confirm(`Are you sure you want to delete "${teamName}"? This will not delete director teams under it, but they will become independent.`)) {
      const result = await removeTeam(teamId);
      if (result.success) {
        showToast('AVP team deleted successfully!');
      } else {
        showToast('Failed to delete team', 'error');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">🏢 Associate VP View</h2>
          {currentTeam && (
            <p className="text-gray-600 mt-1">Team: {currentTeam.name}</p>
          )}
        </div>
        {!currentTeam && (
          <Button onClick={() => setShowCreateTeam(true)} variant="primary">
            + Create AVP Team
          </Button>
        )}
      </div>

      {/* Create Team Modal */}
      {showCreateTeam && (
        <Card title="Create AVP Team">
          <div className="space-y-4">
            <Input
              label="Team Name"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="e.g., Corporate EHS Division"
            />
            <Input
              label="Associate VP Name"
              value={avpName}
              onChange={(e) => setAvpName(e.target.value)}
              placeholder="Your name"
            />
            <div className="flex gap-3">
              <Button onClick={handleCreateTeam} variant="primary">
                Create Team
              </Button>
              <Button onClick={() => setShowCreateTeam(false)} variant="outline">
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Executive Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white p-5 rounded-lg shadow-lg">
          <div className="text-2xl font-bold">{directorTeams.length}</div>
          <div className="text-sm mt-1 opacity-90">Directors</div>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-5 rounded-lg shadow-lg">
          <div className="text-2xl font-bold">{grandTotalManagers}</div>
          <div className="text-sm mt-1 opacity-90">Manager Teams</div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-5 rounded-lg shadow-lg">
          <div className="text-2xl font-bold">{grandTotalFTE.toFixed(1)}</div>
          <div className="text-sm mt-1 opacity-90">Total FTE</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-5 rounded-lg shadow-lg">
          <div className="text-2xl font-bold">{grandTotalActivities}</div>
          <div className="text-sm mt-1 opacity-90">Total Activities</div>
        </div>
      </div>

      {/* Director Rollups */}
      {directorRollups.length > 0 ? (
        <div className="space-y-4">
          {directorRollups.map(({ director, managerCount, totalActivities, totalFTE, condensedActivities, condensedJustification }) => (
            <Card key={director.id} title={
              <div className="flex justify-between items-center">
                <span>Director: {director.managerName}</span>
                <button
                  onClick={() => handleDeleteTeam(director.id, director.name)}
                  className="text-red-600 hover:text-red-800 text-sm px-3 py-1 rounded border border-red-300 hover:bg-red-50"
                >
                  🗑️ Delete Team
                </button>
              </div>
            }>
              <div className="space-y-4">
                {/* Director Summary */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg">
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <span className="text-xs text-gray-600">Team Name</span>
                      <div className="font-semibold text-gray-900">{director.name}</div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-600">Manager Teams</span>
                      <div className="font-semibold text-gray-900">{managerCount}</div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-600">Total Activities</span>
                      <div className="font-semibold text-gray-900">{totalActivities}</div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-600">FTE Required</span>
                      <div className="font-semibold text-green-700 text-xl">{totalFTE.toFixed(2)}</div>
                    </div>
                  </div>
                </div>

                {/* Condensed Activity Breakdown */}
                <div>
                  <div className="text-sm font-semibold text-gray-900 mb-3">📊 Activity Breakdown</div>
                  <div className="space-y-2">
                    {condensedActivities.map((category, idx) => (
                      <div key={idx} className="bg-white border border-gray-200 p-3 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900">{category.category}</span>
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                {category.count} activities
                              </span>
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              Examples: {category.examples.join(', ')}
                              {category.count > 3 && ` +${category.count - 3} more`}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              Total Hours: {category.totalHours}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Condensed Justification */}
                {condensedJustification && (
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <div className="text-sm font-semibold text-purple-900 mb-2">📝 Executive Summary</div>
                    <p className="text-gray-700 text-sm leading-relaxed">{condensedJustification}</p>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🏢</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Director Teams Yet</h3>
            <p className="text-gray-600">
              Director teams will appear here once they are created within the organization.
            </p>
          </div>
        </Card>
      )}

      {/* AVP Executive Summary */}
      {directorRollups.length > 0 && (
        <Card title="AVP Executive Summary">
          <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-blue-50 p-6 rounded-lg">
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Organization Overview</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-indigo-600">{directorTeams.length}</div>
                    <div className="text-xs text-gray-600">Director Teams</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{grandTotalManagers}</div>
                    <div className="text-xs text-gray-600">Manager Teams</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{grandTotalActivities}</div>
                    <div className="text-xs text-gray-600">EHS Activities</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-700">{grandTotalFTE.toFixed(2)}</div>
                    <div className="text-xs text-gray-600">Total FTE</div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-900 mb-2">Resource Allocation</h4>
                <div className="space-y-2">
                  {directorRollups.map(({ director, totalFTE, managerCount }) => {
                    const percentage = grandTotalFTE > 0 ? (totalFTE / grandTotalFTE * 100) : 0;
                    return (
                      <div key={director.id} className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{director.name}</div>
                          <div className="text-xs text-gray-600">{managerCount} manager(s)</div>
                        </div>
                        <div className="w-1/3 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full" 
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-gray-900">{totalFTE.toFixed(1)} FTE</div>
                          <div className="text-xs text-gray-600">{percentage.toFixed(0)}%</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AVPView;
